/**
* AJAX Content Loader & Shortcode Parser
*
* DESCRIPTION:
* This script scans the main article area for specific shortcodes or custom elements,
* fetches content from the provided URLs via AJAX, and injects it into the page.
* It is specifically designed to work with Shopify sections but can be adapted.
*
* HOW TO USE:
*
* Method 1: CMS Shortcode (Recommended for rich text editors)
* Type the following pattern directly into your text editor:
* [ajax-loader link="/pages/your-page-handle" section="template--12345__main"]
* (The 'section' attribute is optional. If omitted, the whole page is fetched.)
*
* Method 2: HTML Custom Element (For developers)
* Use the custom element directly in your liquid/html files:
* <ajax-loader data-url="/pages/your-page-handle" data-section="template--12345__main"></ajax-loader>
*
* Method 3: Paragraph Element
* Use a standard <p> tag with specific data attributes:
* <p data-type="ajax-loader" data-url="/pages/your-page-handle" data-section="template--12345__main"></p>
*
* KEY FEATURES:
* 1. Scope: Only scans within main article containers (defined in MAIN_ARTICLE_SCOPE).
* 2. Cleanup: Automatically removes empty <p> or <span> tags that CMS editors often wrap around shortcodes.
* 3. Styles: Extracts <link rel="stylesheet"> tags from the fetched content and moves them
* to the document <head> (preventing duplicates).
* 4. Events: Runs on initial load and listens for 'shopify:section:load'.
*/

(() => {
const SHORTCODE_SELECTOR = 'ajax-loader, ajax-section, p[data-type="ajax-loader"]';
// Matches [ajax-loader link="..." section="..."] (section is optional)
const SHORTCODE_TEXT_PATTERN = /\[ajax-loader\s+link="([^"]+)"(?:\s+section="([^"]+)")?\s*\]/gi;
const PROCESSED_ATTRIBUTE = 'data-ajax-section-processed';
// Defines where the script looks for shortcodes
const MAIN_ARTICLE_SCOPE = '[data-section-type="main-article"], .section-main-article, .shopify-section--main-article, article.article-template, main article';

const isWithinMainArticle = (element) => Boolean(element.closest(MAIN_ARTICLE_SCOPE));

const removeWrapperIfEmpty = (wrapper) => {
if (!wrapper) {
return;
}

if (wrapper.matches('p, span') && wrapper.textContent.trim() === '' && wrapper.children.length === 0) {
wrapper.remove();
}
};

const removeShortcode = (shortcode) => {
const wrapper = shortcode.closest('p, span');
shortcode.remove();

// Prevent acting on the wrapper if the shortcode itself WAS the wrapper (e.g. <p data-type="ajax-loader">)
if (wrapper && wrapper !== shortcode) {
removeWrapperIfEmpty(wrapper);
}
};

const insertLoadedContent = (shortcode, fragment) => {
const wrapper = shortcode.closest('p, span');

// If the shortcode is wrapped in a P tag (common in Shopify RTE), try to insert outside of it.
// We add wrapper !== shortcode to ensure we aren't targeting the shortcode itself if it IS a <p> tag.
if (wrapper && wrapper !== shortcode && wrapper.parentNode) {
wrapper.parentNode.insertBefore(fragment, wrapper.nextSibling);
shortcode.remove();
removeWrapperIfEmpty(wrapper);
return;
}

// Default replacement (handles custom elements and the <p data-type="ajax-loader"> scenario)
shortcode.replaceWith(fragment);
};

const getRequestUrl = (value) => {
if (!value) {
return null;
}

try {
return new URL(value, window.location.origin);
} catch (error) {
return null;
}
};

// Moves stylesheets from the fetched fragment to the main document head
const moveStylesheetsToHead = (fragment) => {
const head = document.head;

if (!head || !fragment || !fragment.querySelectorAll) {
return;
}

const existingHrefs = new Set(
Array.from(head.querySelectorAll('link[rel~="stylesheet"][href]'))
.map((link) => {
try {
return new URL(link.getAttribute('href'), window.location.origin).toString();
} catch (error) {
return null;
}
})
.filter(Boolean)
);

fragment.querySelectorAll('link[rel~="stylesheet"][href]').forEach((link) => {
const normalizedHref = getRequestUrl(link.getAttribute('href'))?.toString();

if (!normalizedHref) {
link.remove();
return;
}

if (!existingHrefs.has(normalizedHref)) {
const clone = link.cloneNode(true);
clone.setAttribute('href', normalizedHref);
head.appendChild(clone);
existingHrefs.add(normalizedHref);
}

link.remove();
});
};

const fetchAndRenderShortcode = async (shortcode) => {
let urlString = shortcode.getAttribute('data-url')?.trim() ||
shortcode.textContent.trim() ||
shortcode.getAttribute('link')?.trim();

if (!urlString) {
removeShortcode(shortcode);
return;
}

const urlObj = getRequestUrl(urlString);

if (!urlObj) {
removeShortcode(shortcode);
return;
}

// Check for section ID in attribute or data-attribute and append to URL
const sectionId = shortcode.getAttribute('data-section') || shortcode.getAttribute('section');
if (sectionId) {
urlObj.searchParams.set('section_id', sectionId.trim());
}

const requestUrl = urlObj.toString();

try {
const response = await fetch(requestUrl, { credentials: 'same-origin' });

if (!response.ok) {
throw new Error('Ajax section request failed');
}

const html = (await response.text()).trim();

if (!html) {
removeShortcode(shortcode);
return;
}

const template = document.createElement('template');
template.innerHTML = html;

if (!template.content.childNodes.length) {
removeShortcode(shortcode);
return;
}

moveStylesheetsToHead(template.content);

const wrapper = document.createElement('div');
wrapper.classList.add('not-prose');
wrapper.classList.add('ajax-loader');
wrapper.appendChild(template.content);

insertLoadedContent(shortcode, wrapper);
} catch (error) {
console.error('Failed to load ajax section', error);
removeShortcode(shortcode);
}
};

// Scans text nodes to replace [ajax-loader] text with actual DOM elements
const convertShortcodePlaceholders = (root) => {
const textNodes = [];
const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);

while (walker.nextNode()) {
const node = walker.currentNode;

if (!node || !node.textContent || !SHORTCODE_TEXT_PATTERN.test(node.textContent)) {
SHORTCODE_TEXT_PATTERN.lastIndex = 0;
continue;
}

const parentElement = node.parentElement;

// Skip scripts, styles, etc.
if (parentElement && parentElement.closest('script, style, textarea')) {
SHORTCODE_TEXT_PATTERN.lastIndex = 0;
continue;
}

textNodes.push(node);
SHORTCODE_TEXT_PATTERN.lastIndex = 0;
}

textNodes.forEach((node) => {
const fragment = document.createDocumentFragment();
const text = node.textContent || '';
let lastIndex = 0;
SHORTCODE_TEXT_PATTERN.lastIndex = 0;
let match;

while ((match = SHORTCODE_TEXT_PATTERN.exec(text)) !== null) {
const precedingText = text.slice(lastIndex, match.index);

if (precedingText) {
fragment.appendChild(document.createTextNode(precedingText));
}

const url = match[1].trim();
const sectionId = match[2]?.trim(); // Capture optional section

if (url) {
const shortcodeEl = document.createElement('ajax-loader');
shortcodeEl.setAttribute('data-url', url);
if (sectionId) {
shortcodeEl.setAttribute('data-section', sectionId);
}
fragment.appendChild(shortcodeEl);
}

lastIndex = SHORTCODE_TEXT_PATTERN.lastIndex;
}

const trailing = text.slice(lastIndex);

if (trailing) {
fragment.appendChild(document.createTextNode(trailing));
}

node.parentNode?.replaceChild(fragment, node);
SHORTCODE_TEXT_PATTERN.lastIndex = 0;
});
};

const processShortcodes = (root = document) => {
// 1. Convert text [shortcodes] to <ajax-loader> elements
convertShortcodePlaceholders(root);

// 2. Find and process all <ajax-loader> elements, and <p data-type="ajax-loader"> tags
root.querySelectorAll(SHORTCODE_SELECTOR).forEach((shortcode) => {
if (!isWithinMainArticle(shortcode) || shortcode.hasAttribute(PROCESSED_ATTRIBUTE)) {
return;
}

shortcode.setAttribute(PROCESSED_ATTRIBUTE, 'true');
fetchAndRenderShortcode(shortcode);
});
};

if (document.readyState === 'loading') {
document.addEventListener('DOMContentLoaded', () => processShortcodes());
} else {
processShortcodes();
}

// Shopify Theme Editor support
document.addEventListener('shopify:section:load', (event) => {
const target = event.target;

if (target instanceof HTMLElement) {
processShortcodes(target);
}
});
})();